import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useCreateCheckout, useSubscription } from '../../hooks/useSubscription';
import { LaunchIcon, RiseIcon, SummitIcon, getTierColor } from '../icons/TierIcons';

interface PlanFeature {
  name: string;
  launch: string | boolean;
  rise: string | boolean;
  summit: string | boolean;
}

const FEATURES: PlanFeature[] = [
  { name: 'Clients', launch: '25', rise: '100', summit: 'Unlimited' },
  { name: 'Monthly Invoices', launch: '50', rise: '200', summit: 'Unlimited' },
  { name: 'Monthly Contracts', launch: '25', rise: '100', summit: 'Unlimited' },
  { name: 'Projects', launch: '10', rise: '50', summit: 'Unlimited' },
  { name: 'Tasks', launch: '100', rise: '500', summit: 'Unlimited' },
  { name: 'Notes', launch: '250', rise: '1,000', summit: 'Unlimited' },
  { name: 'Landing Pages', launch: '1', rise: '3', summit: 'Unlimited' },
  { name: 'Monthly Leads', launch: '100', rise: '500', summit: 'Unlimited' },
  { name: 'AI Actions/Month', launch: '100', rise: '500', summit: 'Unlimited' },
  { name: 'Templates', launch: '5', rise: '20', summit: 'Unlimited' },
  { name: 'Storage', launch: '5 GB', rise: '25 GB', summit: '100 GB' },
  { name: 'Community Hub', launch: true, rise: true, summit: true },
  { name: 'Analytics', launch: false, rise: true, summit: true },
  { name: 'AI Chat', launch: false, rise: true, summit: true },
  { name: 'Data Export', launch: true, rise: true, summit: true },
  { name: 'White Label', launch: false, rise: false, summit: true },
];

const PLANS = [
  {
    tier: 'launch' as const,
    name: 'Launch',
    description: 'Perfect for getting started',
    monthlyPrice: 29,
    annualMonthlyPrice: 24,
    annualPrice: 288,
    color: '#1976d2',
  },
  {
    tier: 'rise' as const,
    name: 'Rise',
    description: 'For growing businesses',
    monthlyPrice: 59,
    annualMonthlyPrice: 49,
    annualPrice: 588,
    color: '#7c3aed',
    popular: true,
  },
  {
    tier: 'summit' as const,
    name: 'Summit',
    description: 'For established businesses',
    monthlyPrice: 119,
    annualMonthlyPrice: 99,
    annualPrice: 1188,
    color: '#059669',
  },
];

export function PricingPlans() {
  const [isAnnual, setIsAnnual] = useState(false);
  const { data: subscriptionData } = useSubscription();
  const createCheckout = useCreateCheckout();

  const currentTier = subscriptionData?.subscription?.tier;

  const handleSelectPlan = async (tier: 'launch' | 'rise' | 'summit') => {
    try {
      const result = await createCheckout.mutateAsync({
        tier,
        billingCycle: isAnnual ? 'annual' : 'monthly',
      });

      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
    }
  };

  const getButtonText = (planTier: string) => {
    if (currentTier === planTier) return 'Current Plan';
    if (currentTier === 'trial' || currentTier === 'expired') return 'Get Started';
    return 'Upgrade';
  };

  const isCurrentPlan = (planTier: string) => currentTier === planTier;

  return (
    <Box>
      {/* Billing Toggle */}
      <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
        <Typography
          sx={{ fontWeight: isAnnual ? 400 : 600, mr: 1 }}
        >
          Monthly
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isAnnual}
              onChange={(e) => setIsAnnual(e.target.checked)}
              color="primary"
            />
          }
          label=""
        />
        <Typography
          sx={{ fontWeight: isAnnual ? 600 : 400, ml: 1 }}
        >
          Annual
        </Typography>
        {isAnnual && (
          <Chip
            label="Save 17%"
            color="success"
            size="small"
            sx={{ ml: 2 }}
          />
        )}
      </Box>

      {createCheckout.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to start checkout. Please try again.
        </Alert>
      )}

      {/* Plan Cards */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={3}
        justifyContent="center"
      >
        {PLANS.map((plan) => (
          <Card
            key={plan.tier}
            sx={{
              flex: 1,
              maxWidth: { xs: '100%', md: 350 },
              position: 'relative',
              overflow: 'visible',
              border: plan.popular ? `2px solid ${plan.color}` : '1px solid #e0e0e0',
              boxShadow: plan.popular ? 4 : 1,
            }}
          >
            {plan.popular && (
              <Chip
                label="Most Popular"
                color="primary"
                size="small"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                  fontWeight: 600,
                  bgcolor: plan.color,
                }}
              />
            )}
            <CardContent sx={{ p: 3 }}>
              {/* Tier Icon */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                {plan.tier === 'launch' && <LaunchIcon sx={{ fontSize: 48, color: getTierColor('launch') }} />}
                {plan.tier === 'rise' && <RiseIcon sx={{ fontSize: 48, color: getTierColor('rise') }} />}
                {plan.tier === 'summit' && <SummitIcon sx={{ fontSize: 48, color: getTierColor('summit') }} />}
              </Box>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: plan.color, textAlign: 'center' }}
              >
                {plan.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2} textAlign="center">
                {plan.description}
              </Typography>

              <Box display="flex" alignItems="baseline" mb={3} justifyContent="center">
                <Typography variant="h3" fontWeight="bold">
                  ${isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice}
                </Typography>
                <Typography variant="body1" color="text.secondary" ml={1}>
                  /month
                </Typography>
              </Box>

              {isAnnual && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Billed ${plan.annualPrice}/year
                </Typography>
              )}

              <Button
                variant={isCurrentPlan(plan.tier) ? 'outlined' : 'contained'}
                fullWidth
                size="large"
                disabled={isCurrentPlan(plan.tier) || createCheckout.isPending}
                onClick={() => handleSelectPlan(plan.tier)}
                sx={{
                  mb: 3,
                  bgcolor: isCurrentPlan(plan.tier) ? 'transparent' : plan.color,
                  '&:hover': {
                    bgcolor: isCurrentPlan(plan.tier)
                      ? 'transparent'
                      : plan.color,
                    opacity: 0.9,
                  },
                }}
              >
                {createCheckout.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  getButtonText(plan.tier)
                )}
              </Button>

              <List dense>
                {FEATURES.slice(0, 8).map((feature) => {
                  const value = feature[plan.tier];
                  const hasFeature = value !== false;
                  return (
                    <ListItem key={feature.name} sx={{ px: 0, py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {hasFeature ? (
                          <CheckIcon color="success" fontSize="small" />
                        ) : (
                          <CloseIcon color="disabled" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          typeof value === 'string'
                            ? `${feature.name}: ${value}`
                            : feature.name
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: hasFeature ? 'text.primary' : 'text.disabled',
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}

export default PricingPlans;
